import {
  Button,
  Checkbox,
  CheckboxGroup,
  CloseButton,
  Flex,
  Heading,
  IconDownloadLine,
  IconImportLine,
  Link,
  List,
  Modal,
  Text,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useContext, useState } from "react";
import { Trans } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { Files } from "../../../services/FilesService";
import { TaskService } from "../../../services/TaskService";
import { downloadCSVTemplate } from "../../../utils/DownloadCSVTemplate";
import {
  csvFileEnricher,
  fileReader,
  fileWriter,
} from "../../../utils/FileUtils";
import { awaitTask, waitForExpectedResult } from "../../../utils/Timer";
import { Card } from "../../Card";
import CurriculumMapTermSelect from "../../CurriculumMap/CurriculumMapTermSelect";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../CustomHooks/NotificationProvider";
import {
  FAIL,
  GOOD,
  WORKING,
  useStatus,
} from "../../CustomHooks/StatusProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import { getEarliestCanvasTerm } from "../../TermManagement/utils";
import EnrichedFileDrop from "../FileDrop/EnrichedFileDrop";
import {
  getCurrentVersion,
  getFutureVersions,
  isArchived,
  pendingArchive,
} from "../utils";
import { CategoriesContext, UncategorizedContext } from "./CategoryList";
import { enrichPLOs } from "./utils";

const LEARNING_OUTCOMES_FILE_TYPE = "learningOutcomes";

const PLOUploadModal = ({ program }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { updateStatus, toggleStatus } = useStatus();
  const { categories } = useContext(CategoriesContext);
  const { uncategorizedLos } = useContext(UncategorizedContext);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [LOFile, setLOFile] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [effectiveTerm, setEffectiveTerm] = useState({});
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleSubmit = async () => {
    try {
      // Enrich the file on submit
      const termId = getEarliestCanvasTerm(effectiveTerm)?.id;
      const file = LOFile;
      const fr = await fileReader(file);
      const content = fr.result;
      const enrichedContent = csvFileEnricher(
        content,
        enrichPLOs(program, termId, templateOptions.includes("autoApprove"))
      );
      const enrichedFile = fileWriter(
        enrichedContent,
        LOFile.name,
        LOFile.type
      );

      const { taskId } = await Files.saveFile(
        enrichedFile,
        LEARNING_OUTCOMES_FILE_TYPE,
        null,
        true
      );

      setUploadDialog(false);
      setLOFile(null);
      fetchTask(taskId);
    } catch (error) {
      console.debug(error);
      showNotification(t("learningOutcomes.uploadError"), ERROR);
    }
  };

  const fetchTask = async (taskId) => {
    showNotification(
      <Text>
        {t("notifications.plo.ploUploadInProgress")}
        <Link
          themeOverride={{ fontWeight: "bold" }}
          onClick={() => toggleStatus(true)}
        >
          {t("status.checkStatus")}
        </Link>
      </Text>,
      SUCCESS,
      10000
    );
    updateStatus({
      service: {
        name: t("status.ploUploadStatus"),
        status: WORKING,
        progress: 0,
      },
    });

    await awaitTask(taskId);

    waitForExpectedResult(
      () => TaskService.getTaskById(taskId),
      (task) => {
        const progress = parseInt(
          (task.counter / task.obj.total_outcomes) * 100
        );
        updateStatus({
          service: {
            name: t("status.ploUploadStatus"),
            status: progress === 100 ? GOOD : WORKING,
            progress: progress,
          },
        });
        return task.status === "done" || task.status === "error";
      },
      processPushFileTask,
      () => {
        showNotification(t("notifications.plo.ploUploadFailed"), ERROR);
        updateStatus({
          service: { name: t("status.ploUploadStatus"), status: FAIL },
        });
      },
      300,
      3000
    );
  };

  function processPushFileTask(task) {
    if (task.status === "done") {
      showNotification(t("notifications.plo.ploUploadComplete"), SUCCESS);
    } else if (task.status === "error") {
      showNotification(t("notifications.plo.ploUploadFailed"), ERROR);
      updateStatus({
        service: { name: t("status.ploUploadStatus"), status: FAIL },
      });
    }
  }

  const handleTemplateOptionsChange = (value) => {
    if (value.includes("versionArchive")) {
      setTemplateOptions(["versionArchive", "autoApprove"]);
    } else {
      setTemplateOptions(value);
      setEffectiveTerm({});
    }
  };

  const handleDownload = () => {
    try {
      const headers = [
        "current_version_outcome_id",
        "category_name",
        "category_id",
        "outcome_variety",
        "title",
        "description",
        "future_version_exists",
        "new_title",
        "new_description",
        "archive",
        "effective_term_name",
      ];

      const ploData = [];
      uncategorizedLos.forEach((plo) => {
        if (!plo.unapproved && !isArchived(plo) && !pendingArchive(plo)) {
          const currentPlo = getCurrentVersion(plo);
          ploData.push({
            current_version_outcome_id: currentPlo.id,
            category_name: "",
            category_id: "",
            outcome_variety: currentPlo.outcome_variety,
            title: currentPlo.name,
            description: currentPlo.statement,
            future_version_exists: getFutureVersions(plo).length > 0,
            new_title: "",
            new_description: "",
            archive: "",
            effective_term_name: effectiveTerm.name,
          });
        }
      });
      categories.forEach((category) => {
        category.outcomes.forEach((plo) => {
          if (!plo.unapproved && !isArchived(plo) && !pendingArchive(plo)) {
            const currentPlo = getCurrentVersion(plo);
            ploData.push({
              current_version_outcome_id: currentPlo.id,
              category_name: category.name,
              category_id: category.id,
              outcome_variety: currentPlo.outcome_variety,
              title: currentPlo.name,
              description: currentPlo.statement,
              future_version_exists: getFutureVersions(plo).length > 0,
              new_title: "",
              new_description: "",
              archive: "",
              effective_term_name: effectiveTerm.name,
            });
          }
        });
      });
      downloadCSVTemplate("PLO_Template.csv", headers, ploData);
    } catch {
      showNotification(t("notifications.general.downloadError"), ERROR);
    }
  };

  const confirmationText = () => {
    let option = "";
    if (
      !templateOptions.includes("versionArchive") &&
      !templateOptions.includes("autoApprove")
    ) {
      option = "in an unapproved state";
    } else if (
      !templateOptions.includes("versionArchive") &&
      templateOptions.includes("autoApprove")
    ) {
      option = "auto-approved, as new";
    } else if (
      templateOptions.includes("versionArchive") &&
      templateOptions.includes("autoApprove")
    ) {
      option = "auto-approved, as versions/archived for " + effectiveTerm.name;
    }
    return (
      t("learningOutcomes.confirmMessage1") +
      option +
      ". " +
      t("learningOutcomes.confirmMessage2")
    );
  };

  return (
    <>
      <Button
        id="upload-outcome-button"
        color="primary"
        renderIcon={IconImportLine}
        onClick={() => setUploadDialog(true)}
      >
        {t("plo.uploadButton")}
      </Button>
      <Modal
        open={uploadDialog}
        label={t("global.dragFile")}
        size="large"
        onExited={() => {
          setLOFile(null);
          setTemplateOptions([]);
          setEffectiveTerm({});
        }}
      >
        <Modal.Header>
          <Heading level="h2" as="h1">
            {t("learningOutcomes.ploUpload")}
          </Heading>
          <CloseButton
            placement="end"
            offset="medium"
            screenReaderLabel={t("global.close")}
            onClick={() => setUploadDialog(false)}
          />
        </Modal.Header>
        <Modal.Body>
          <EnrichedFileDrop
            fileState={LOFile}
            setFileState={setLOFile}
            fileDropHeading={t("plo.dropFileHeading")}
            fileExtension=".csv"
          />
          <Card style={{ margin: "20px 0" }}>
            <Flex wrap="wrap" gap="small">
              <Flex.Item size="100%">
                <CheckboxGroup
                  name="templateOptions"
                  description={t("learningOutcomes.templateOptions")}
                  size="small"
                  value={templateOptions}
                  onChange={handleTemplateOptionsChange}
                >
                  <Checkbox
                    label={t("learningOutcomes.autoApprove")}
                    value="autoApprove"
                    disabled={templateOptions.includes("versionArchive")}
                  />
                  <Checkbox
                    label={t("learningOutcomes.versionArchive")}
                    value="versionArchive"
                  />
                </CheckboxGroup>
              </Flex.Item>
            </Flex>
          </Card>
          <div style={{ marginTop: 10 }}>
            <Text as="div">{t("learningOutcomes.predefinedStructure")}</Text>
            <br />
            {templateOptions.includes("versionArchive") ? (
              <>
                <Text as="div">{t("plo.downloadText")}</Text>
                <List margin="small 0">
                  <List.Item>{t("versionArchive.item1")}</List.Item>
                  <List.Item>{t("versionArchive.item2")}</List.Item>
                  <List.Item>{t("versionArchive.item3")}</List.Item>
                  <List.Item>{t("versionArchive.item4")}</List.Item>
                </List>
                <Text as="div" weight="bold">
                  {t("versionArchive.footer1")}
                </Text>
                <br />
                <Text as="div" fontStyle="italic">
                  {t("versionArchive.footer2")}
                </Text>
                <br />
                <Trans i18nKey="versionArchive.dataDictionary">
                  A data dictionary for the selected template options is
                  available
                  <a href="/versionArchiveDataDictionary.txt" download>
                    here
                  </a>
                  .
                </Trans>
              </>
            ) : (
              <>
                <Text as="div">
                  {t("learningOutcomes.uploadLine1_1")}
                  <Text weight="bold">
                    {t("learningOutcomes.uploadLine1_2", { type: "PLO" })}
                  </Text>
                  {t("learningOutcomes.uploadLine1_3")}
                  <Text weight="bold">object_type</Text>
                  {t("learningOutcomes.uploadLine1_4")}
                </Text>
                <br />
                <Text as="div">{t("learningOutcomes.uploadLine2")}</Text>
                <br />
                <Text as="div">
                  {t("learningOutcomes.uploadLine3", { type: "PLO" })}
                </Text>
              </>
            )}
            <View as="div" borderWidth="small 0 0 0" margin="small 0" />
            <Flex wrap="wrap" gap="small" alignItems="end">
              {templateOptions.includes("versionArchive") ? (
                <>
                  <Flex.Item>
                    <CurriculumMapTermSelect
                      id="effective-term-select"
                      label={t("learningOutcomes.effectiveTerm")}
                      selectHandler={(term) => setEffectiveTerm(term)}
                      selectedTermId={effectiveTerm.id}
                    />
                  </Flex.Item>
                  <Flex.Item>
                    <Button
                      color="primary"
                      renderIcon={IconDownloadLine}
                      onClick={handleDownload}
                      disabled={
                        templateOptions.includes("versionArchive") &&
                        !effectiveTerm.id
                      }
                    >
                      {t("plo.downloadTemplate")}
                    </Button>
                  </Flex.Item>
                </>
              ) : (
                <Flex.Item>
                  <Text as="div" lineHeight="double">
                    <Link href="/sampleLOs1.csv">
                      {t("learningOutcomes.downloadExampleFile1")}
                    </Link>
                  </Text>
                  <Text as="div" lineHeight="double">
                    <Link href="/sampleLOs2.csv">
                      {t("learningOutcomes.downloadExampleFile2")}
                    </Link>
                  </Text>
                  <Text as="div" lineHeight="double">
                    <Link href="/sampleLOs3.csv">
                      {t("learningOutcomes.downloadExampleFile3")}
                    </Link>
                  </Text>
                </Flex.Item>
              )}
            </Flex>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button margin="0 small 0 0" onClick={() => setUploadDialog(false)}>
            {t("global.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={() => setOpenConfirm(true)}
            disabled={
              !LOFile ||
              (templateOptions.includes("versionArchive") && !effectiveTerm.id)
            }
          >
            {t("global.submit")}
          </Button>
        </Modal.Footer>
      </Modal>
      <DialogYesNo
        message={confirmationText()}
        openStatus={openConfirm}
        onClickNo={() => setOpenConfirm(false)}
        onClickYes={async () => {
          await handleSubmit();
          setOpenConfirm(false);
        }}
      />
    </>
  );
};

export default PLOUploadModal;
